/**
 * RAVEN - Auction Tool: PackageItemList.jsx
 * [Component for showing and managing Auction Package Items]
 * - Handles item selection, sorting, pagination, and renders action buttons.
 * - Used in both "edit" and "liveAuction" modes.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Buffer } from "buffer";
import isEqual from "lodash/isEqual";

// Custom hooks
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	IconButton,
	Pagination,
	Switch,
	Table,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "../UI/RecordNav/RecordNav";
import styles from "./PackageItemList.module.css";
import { EnhancedTableHead, EnhancedTableToolbar } from "../UI/common/EnhancedTableParts";
import {
	ChevronLeftRegular,
	ChevronRightRegular,
	EditRegular,
	MoneyHandRegular,
	EyeRegular,
} from "@fluentui/react-icons";

// Utilities
import { getValuesByPropertyPath, findObjectByProperty, deepClone } from "src/utils/dataUtils";
import { Session, isDevTeam, isAdmin } from "src/utils/sessionUtils";
import { debugLog, showTesting, isDevSiteNow } from "src/utils/debugUtils";
import { applyFormatToNumber, getShortDateTimeString } from "src/utils/formatUtils";

/**
 * Main component for displaying the list of package items.
 * Handles table state, item selection, and renders action controls per row.
 *
 * @param {object} props - { item, itemSelected, onSelectItem }
 */
const PackageItemList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const imgPlaceholder = "/No_Image_Available.jpg";

	// --- Local State ---
	const [currPackage, setCurrPackage] = useState(props.item || null);
	const [itemsList, setItemsList] = useState(
		// Map baseline items to package items (syncs package-level auction info onto baseline items)
		props.item?.baseline?.baseline_items?.map((xOrig) => {
			const x = { ...xOrig };
			const pItem = findObjectByProperty(currPackage?.auc_package_items, "baseline_item_id", x.id);
			if (pItem) {
				x.current_price = pItem.current_price;
				x.highest_bidder_id = pItem.highest_bidder_id;
				x.HighestBidder = pItem.HighestBidder;
				x.lastBidAt =
					pItem.auc_bids && pItem.auc_bids.length > 0 ? pItem.auc_bids[0].createdAt : null;
				x.numBidPlaced = pItem.auc_bids.length || "";
				x.package_item_id = pItem.id;
			}

			return x;
		}) || [],
	);
	const pageMode = props.mode || "liveAuction";
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState(currPackage?.aucItemSelected || []);

	useEffect(() => {
		// Track mode changes because edit/liveAuction drives which controls and rows stay visible.
		debugLog(2, "[PackageItemList][STATE] pageMode changed", { pageMode: pageMode });
	}, [pageMode]);

	// --- Helper to return filtered auction items based on Active/Inactive toggle
	const visibleItems = useMemo(() => {
		return showInactive ? itemsList : itemsList.filter((x) => x.status_id === 1);
	}, [itemsList, showInactive]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		setItemSelected(newSelected);
		// Only call parent if different
		if (!isEqual(currPackage?.aucItemSelected, newSelected)) {
			props.onSelectItem(newSelected);
		}
		debugLog(3, "[PackageItemList][ACTION] handleSelectionChange", { newSelected: newSelected });
	};

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		page,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleItems, // Or from Redux selector
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
	});

	// --- Table Columns
	const headCells = [
		{
			id: "empty1",
			numeric: false,
			disablePadding: false,
			label: "",
			disableSort: true,
		},
		{
			id: "empty2",
			numeric: false,
			disablePadding: false,
			label: "",
			disableSort: true,
		},
		{
			id: "thumbnail",
			numeric: false,
			disablePadding: false,
			label: "",
			disableSort: true,
		},
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "military_service_id",
			numeric: false,
			disablePadding: false,
			label: "Military Service",
		},
		{
			id: "domain_id",
			numeric: false,
			disablePadding: false,
			label: "Domain",
		},
		{
			id: "category_id",
			numeric: false,
			disablePadding: false,
			label: "Category",
		},
		{
			id: "current_price",
			numeric: false,
			disablePadding: false,
			label: "Current Price",
		},
		{
			id: "highest_bidder_id",
			numeric: false,
			disablePadding: false,
			label: "Highest Bidder",
		},
		{
			id: "number_bids",
			numeric: false,
			disablePadding: false,
			label: "# Bid Placed",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Bid",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Baseline Item Status",
			align: "center",
		},
	];
	let adjustedHeadCells = [...headCells];
	if (pageMode === "liveAuction") {
		adjustedHeadCells = adjustedHeadCells.slice(1); // remove first column
	}

	// --- Utility Functions ---
	// check if an item is "new"
	const isNew = (id) =>
		getValuesByPropertyPath(currPackage?.auc_package_items, "baseline_item_id").indexOf(id) === -1;

	// is current user a bidder in this package?
	const isUserBidder = (pkg) => {
		const bidders = pkg.auc_package_bidders
			? getValuesByPropertyPath(pkg.auc_package_bidders, "user_id")
			: [];

		return bidders.indexOf(Session().currUser.id) !== -1;
	};

	// --- Effect: respond to parent item changes (deep mapping)
	useEffect(() => {
		const clonePackage = deepClone(props.item);
		setCurrPackage(clonePackage);

		//update the PackageItemList
		const tmpList =
			props.item?.baseline.baseline_items?.map((xOrig) => {
				const x = { ...xOrig };
				const pItem = findObjectByProperty(clonePackage?.auc_package_items, "baseline_item_id", x.id);
				if (pItem) {
					x.current_price = pItem.current_price;
					x.highest_bidder_id = pItem.highest_bidder_id;
					x.HighestBidder = pItem.HighestBidder;
					x.lastBidAt =
						pItem.auc_bids && pItem.auc_bids.length > 0 ? pItem.auc_bids[0].createdAt : null;
					x.numBidPlaced = pItem.auc_bids.length || "";
					x.package_item_id = pItem.id;
				}
				return x;
			}) || [];

		const cList = pageMode === "edit" ? tmpList : tmpList.filter((x) => isSelected(x.id));
		setItemsList([...cList]);

		debugLog(3, "[PackageItemList][STATE] useEffect props.item", {
			propsItem: props.item,
			tmpList: tmpList,
			cList: cList,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.item]);

	// --- Effect: update item list on page mode switch (edit vs liveAuction)
	useEffect(() => {
		const cList = pageMode === "edit" ? itemsList : itemsList.filter((x) => isSelected(x.id));
		setItemsList([...cList]);

		debugLog(3, "[PackageItemList][STATE] useEffect pageMode", {
			pageMode: pageMode,
			itemsList: itemsList,
			cList: cList,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageMode]);

	// --- Effect: update selection from props only if truly changed
	useEffect(() => {
		if (!isEqual(itemSelected, currPackage?.aucItemSelected || [])) {
			setItemSelected(currPackage?.aucItemSelected || []);
		}

		debugLog(3, "[PackageItemList][STATE] useEffect currPackage.aucItemSelected", {
			currPackageAucItemSelected: currPackage?.aucItemSelected,
			itemSelected: itemSelected,
		});
		// eslint-disable-next-line
	}, [currPackage?.aucItemSelected]);

	// --- Handlers ---
	const rowClickHandler = (event, row) => {
		event.preventDefault();
		debugLog(3, "[PackageItemList][ACTION] rowClickHandler", { row: row });
		// notify parent to display the baseline item
		props.onShowItem(row);
	};

	const showAuctionHandler = (event, item) => {
		event.preventDefault();
		debugLog(3, "[PackageItemList][ACTION] showAuctionHandler", { item: item });

		// notify parent to display this package item on auction page
		props.onShowAuction(item);
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[PackageItemList][ACTION] toggleInactiveHandler", { option: option });
		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[PackageItemList][ACTION] sortByHandler", { option: option });
		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[PackageItemList][ACTION] changeRowsPerPageHandler", { option: option });
		setRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[PackageItemList][FLOW] render", {
		renderCount: renderCount.current,
		itemsCount: itemsList?.length ?? 0,
		itemSelectedCount: itemSelected?.length ?? 0,
		visibleItemCount: visibleItems?.length ?? 0,
		rowsPerPage: rowsPerPage,
		page: page,
		showInactive: showInactive,
		pageMode: pageMode,
	});

	return (
		<Box w="100%">
			<Text
				as="div"
				style={{
					flex: "1 1 25%",
					paddingTop: "50px",
					fontSize: "18px",
					fontWeight: 400,
				}}
			>
				Package Items
			</Text>
			<RecordNav
				data={visibleItems}
				{...(pageMode === "edit" ? { showToggleInactive: true } : {})}
				showBack
				showSortBy
				showRowsPerPage
				rowsPerPage={rowsPerPage}
				count={visibleRows.length}
				onToggleInactive={toggleInactiveHandler}
				onSortBy={sortByHandler}
				onChangeRowsPerPage={changeRowsPerPageHandler}
			/>
			<Box w="100%" mb={2} boxShadow="sm" bg="white" rounded="md">
				{showTesting() && isDevTeam() && <EnhancedTableToolbar numSelected={itemSelected.length} />}
				<Box overflowX="auto">
					<Table.Root size={dense ? "sm" : "md"} variant="outline" aria-labelledby="tableTitle">
						<EnhancedTableHead
							numSelected={itemSelected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={visibleItems.length}
							headCells={adjustedHeadCells}
							styles={styles}
							showCheckBox={true}
						/>
						<Table.Body>
							{visibleRows.map((row) => {
								const isItemSelected = isSelected(row.id);

								let strMilitaryService = "";
								let strDomain = "";
								let strCategory = "";
								let tempObj = {};
								if (row.military_service_id) {
									tempObj = findObjectByProperty(
										currPackage.baseline.baseline_configs,
										"id",
										parseInt(row.military_service_id),
									);
									if (tempObj) strMilitaryService = tempObj.comp_value;
								}
								if (row.domain_id) {
									tempObj = findObjectByProperty(
										currPackage.baseline.baseline_configs,
										"id",
										parseInt(row.domain_id),
									);
									if (tempObj) strDomain = tempObj.comp_value;
								}
								if (row.category_id) {
									tempObj = findObjectByProperty(
										currPackage.baseline.baseline_configs,
										"id",
										parseInt(row.category_id),
									);
									if (tempObj) strCategory = tempObj.comp_value;
								}
								const hasDescription = row.description?.trim?.();
								const thumbnail = (
									<img
										alt="not found"
										width={
											row?.thumbnail?.data && row.thumbnail.data.length !== 0
												? "160px"
												: "80px"
										}
										src={
											row?.thumbnail?.data && row.thumbnail.data.length !== 0
												? `data:image/*;base64,${Buffer.from(row.thumbnail.data).toString("base64")}`
												: imgPlaceholder
										}
									/>
								);
								const itemName = <span style={{ cursor: "pointer" }}>{row.name}</span>;

								return (
									<Table.Row
										key={row.id}
										aria-checked={isItemSelected}
										tabIndex={-1}
										selected={isItemSelected}
										// Use the default arrow across the row so non-clickable data cells do
										// not show the browser text cursor; actionable cells override to pointer.
										style={{ cursor: "default" }}
										className={styles.tableBodyRow}
									>
										{(isAdmin() || isDevTeam()) && pageMode === "edit" && (
											<>
												<Table.Cell>
													<MetreaTooltip
														content={`Include ${row.name} in the auction`}
													>
														<Checkbox.Root
															checked={isItemSelected}
															onCheckedChange={(next) =>
																checkHandler(
																	{
																		target: {
																			checked:
																				next === true ||
																				next?.checked === true ||
																				next?.detail?.checked ===
																					true,
																		},
																	},
																	row.id,
																)
															}
															style={{
																display: "inline-flex",
																alignItems: "center",
															}}
														>
															<Checkbox.HiddenInput />
															<Checkbox.Control />
														</Checkbox.Root>
													</MetreaTooltip>
												</Table.Cell>
											</>
										)}

										<Table.Cell>
											{pageMode === "liveAuction" ? (
												<MetreaTooltip
													content={`${
														isUserBidder(currPackage)
															? "Start Bidding"
															: "View Bidding Progress"
													} for Item: ${row.name}`}
												>
													<span>
														<IconButton
															size="md"
															color="#3b6c83 !important"
															variant="ghost"
															onClick={(event) =>
																showAuctionHandler(event, row)
															}
														>
															{isUserBidder(currPackage) ? (
																<MoneyHandRegular />
															) : (
																<EyeRegular />
															)}
														</IconButton>
													</span>
												</MetreaTooltip>
											) : (
												<span>
													{/* Keep a hidden spacer action so the edit-mode table columns stay aligned
													    even when an item is not currently selected into the package. */}
													{isSelected(row.id) ? (
														<MetreaTooltip
															content={
																isNew(row.id)
																	? "Save the package first"
																	: "Edit Auction Item details"
															}
														>
															<IconButton
																size="md"
																color="#3b6c83 !important"
																variant="ghost"
																onClick={(event) =>
																	showAuctionHandler(event, row)
																}
																{...(isNew(row.id) ? { disabled: true } : {})}
															>
																<EditRegular />
															</IconButton>
														</MetreaTooltip>
													) : (
														<IconButton
															size="md"
															variant="ghost"
															aria-hidden
															tabIndex={-1}
															style={{ visibility: "hidden" }}
															// no onClick; it's a visual spacer only
														>
															<EditRegular />
														</IconButton>
													)}
												</span>
											)}
										</Table.Cell>

										<Table.Cell
											component="th"
											style={{
												minWidth: "140px",
												width: "20%",
												paddingX: "1.2em",
												cursor: "pointer",
											}}
											onClick={(event) => rowClickHandler(event, row)}
										>
											{hasDescription ? (
												<MetreaTooltip content={row.description}>
													{thumbnail}
												</MetreaTooltip>
											) : (
												thumbnail
											)}
										</Table.Cell>
										<Table.Cell
											style={{ cursor: "pointer" }}
											onClick={(event) => rowClickHandler(event, row)}
										>
											{hasDescription ? (
												<MetreaTooltip content={row.description}>
													{/* Preserve the old triggerProps cursor affordance on the trigger text. */}
													{itemName}
												</MetreaTooltip>
											) : (
												itemName
											)}
										</Table.Cell>
										<Table.Cell style={{ textAlign: "left" }}>
											{strMilitaryService}
										</Table.Cell>
										<Table.Cell style={{ textAlign: "left" }}>{strDomain}</Table.Cell>
										<Table.Cell style={{ textAlign: "left" }}>{strCategory}</Table.Cell>
										<Table.Cell>
											{applyFormatToNumber(
												parseFloat(row?.current_price ?? 0),
												2,
												"$",
												null,
												false,
											)}
										</Table.Cell>
										<Table.Cell>
											{row.HighestBidder && row.HighestBidder.full_name}
										</Table.Cell>
										<Table.Cell>{row.numBidPlaced}</Table.Cell>
										<Table.Cell style={{ fontSize: "0.7rem" }}>
											{getShortDateTimeString(row?.lastBidAt)}
										</Table.Cell>
										<Table.Cell>{row.status_id === 1 ? "Active" : "Inactive"}</Table.Cell>
									</Table.Row>
								);
							})}
							{emptyRows > 0 && (
								<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
									<Table.Cell
										colSpan={(showTesting() && isDevTeam() ? 1 : 0) + headCells.length}
										style={{ borderBottom: "none", padding: 0 }}
									/>
								</Table.Row>
							)}
						</Table.Body>
					</Table.Root>
				</Box>
			</Box>

			<Box className="flex-auto justify-items-center p-1">
				<Pagination.Root
					count={visibleItems.length}
					pageSize={Number(rowsPerPage) || 10}
					page={pageM1}
					onPageChange={({ page }) => handleChangePageM1(undefined, page)}
				>
					<ButtonGroup variant="ghost" size="2xs">
						<Pagination.PrevTrigger asChild>
							<IconButton>
								<ChevronLeftRegular />
							</IconButton>
						</Pagination.PrevTrigger>
						<Pagination.Items
							render={(page) => (
								<IconButton variant={{ base: "ghost", _selected: "solid" }}>
									{page.value}
								</IconButton>
							)}
						/>
						<Pagination.NextTrigger asChild>
							<IconButton>
								<ChevronRightRegular />
							</IconButton>
						</Pagination.NextTrigger>
					</ButtonGroup>
				</Pagination.Root>
			</Box>

			{showTesting() && isDevTeam() && isDevSiteNow() && (
				<Switch.Root
					size="sm"
					className="m-[10px]"
					checked={dense}
					onCheckedChange={(next) =>
						handleChangeDense({
							target: {
								checked:
									next === true || next?.checked === true || next?.detail?.checked === true,
							},
						})
					}
				>
					<Switch.HiddenInput />
					<Switch.Control>
						<Switch.Thumb />
					</Switch.Control>
					<Switch.Label>Dense padding</Switch.Label>
				</Switch.Root>
			)}
		</Box>
	);
};

export default PackageItemList;
